import { BookingAccountIdentity } from "./BookingAccountIdentity";
import { DateTime } from "luxon";
import { useEffect, useMemo, useState } from "react";
import type { ProviderBooking, ProviderVenue } from "../../lib/api";
import { useAppPreferences } from "../../lib/preferences";
import { calculateIntervalTotalAmount } from "../../lib/provider-pricing";
import { formatEgp } from "../provider-orders/order-status";
import { formatMinutes } from "../provider-sessions/WalkInDialog";

export interface ProviderBookingDraft {
  unit_id: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  scheduled_start_local: string;
  scheduled_end_local: string;
  notes: string;
}

const LENGTHS = [60, 90, 120, 180];

function getDefaultUnitId(
  selectedVenue: ProviderVenue | null,
  preferredUnitId: string | null,
) {
  return (
    selectedVenue?.units.find((unit) => unit.id === preferredUnitId)?.id ??
    selectedVenue?.units[0]?.id ??
    ""
  );
}

export function createEmptyBookingDraft(
  selectedVenue: ProviderVenue | null,
  preferredUnitId: string | null,
): ProviderBookingDraft {
  return {
    unit_id: getDefaultUnitId(selectedVenue, preferredUnitId),
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    scheduled_start_local: "",
    scheduled_end_local: "",
    notes: "",
  };
}

/**
 * A sensible default start, as "YYYY-MM-DDTHH:mm" in the venue's time: the next half
 * hour while the venue is open, otherwise its next opening time.
 */
function defaultStartLocal(venue: ProviderVenue | null, timeZone: string) {
  const now = DateTime.now().setZone(timeZone);
  const nextHalfHour = now.plus({ minutes: 30 - (now.minute % 30) }).set({ second: 0, millisecond: 0 });
  for (let offset = -1; offset <= 7; offset += 1) {
    const day = now.startOf("day").plus({ days: offset });
    const hours = venue?.operating_hours.find((entry) => entry.day_of_week === day.weekday % 7);
    if (!hours?.is_open || !hours.open_time || !hours.close_time) continue;
    const [openH, openM] = hours.open_time.split(":").map(Number);
    const [closeH, closeM] = hours.close_time.split(":").map(Number);
    const open = day.set({ hour: openH, minute: openM });
    let close = day.set({ hour: closeH, minute: closeM });
    if (close.toMillis() <= open.toMillis()) close = close.plus({ days: 1 });
    if (now.toMillis() < open.toMillis()) return open.toFormat("yyyy-LL-dd'T'HH:mm");
    if (nextHalfHour.toMillis() < close.toMillis()) return nextHalfHour.toFormat("yyyy-LL-dd'T'HH:mm");
  }
  return nextHalfHour.toFormat("yyyy-LL-dd'T'HH:mm");
}

function addMinutesLocal(local: string, minutes: number, timeZone: string) {
  const start = DateTime.fromISO(local, { zone: timeZone });
  return start.isValid ? start.plus({ minutes }).toFormat("yyyy-LL-dd'T'HH:mm") : "";
}

function minutesBetween(startLocal: string, endLocal: string, timeZone: string) {
  const start = DateTime.fromISO(startLocal, { zone: timeZone });
  const end = DateTime.fromISO(endLocal, { zone: timeZone });
  if (!start.isValid || !end.isValid) return null;
  return Math.round(end.diff(start, "seconds").seconds / 60);
}

/**
 * New or edited booking: unit, day, start time and a length, with the price shown
 * before saving. The draft keeps the local start/end strings the API expects.
 */
export function BookingFormCard({
  booking,
  initialValue,
  isSubmitting,
  onCancel,
  onSubmit,
  preferredUnitId,
  selectedVenue,
  submitLabel,
  title,
}: {
  booking?: ProviderBooking | null;
  initialValue: ProviderBookingDraft;
  isSubmitting: boolean;
  onCancel?: () => void;
  onSubmit: (value: ProviderBookingDraft) => Promise<void>;
  preferredUnitId: string | null;
  selectedVenue: ProviderVenue | null;
  submitLabel: string;
  title: string;
}) {
  const { t, language } = useAppPreferences();
  const timeZone = selectedVenue?.timezone ?? "UTC";
  const [form, setForm] = useState<ProviderBookingDraft>(initialValue);
  const [length, setLength] = useState<number | "custom">(60);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const start = initialValue.scheduled_start_local || defaultStartLocal(selectedVenue, timeZone);
    const existing = initialValue.scheduled_end_local
      ? minutesBetween(start, initialValue.scheduled_end_local, timeZone)
      : null;
    const nextLength = existing === null ? 60 : LENGTHS.includes(existing) ? existing : "custom";
    setLength(nextLength);
    setForm({
      ...initialValue,
      unit_id: initialValue.unit_id || getDefaultUnitId(selectedVenue, preferredUnitId),
      scheduled_start_local: start,
      scheduled_end_local: initialValue.scheduled_end_local || addMinutesLocal(start, 60, timeZone),
    });
    setError(null);
  }, [initialValue]);

  const units = selectedVenue?.units ?? [];
  const unit = units.find((entry) => entry.id === form.unit_id) ?? null;
  const [day, time] = form.scheduled_start_local.split("T");
  const endTime = form.scheduled_end_local.split("T")[1] ?? "";
  const duration = minutesBetween(form.scheduled_start_local, form.scheduled_end_local, timeZone);

  const estimate = useMemo(() => {
    if (!unit || duration === null || duration <= 0) return null;
    const start = DateTime.fromISO(form.scheduled_start_local, { zone: timeZone });
    const end = DateTime.fromISO(form.scheduled_end_local, { zone: timeZone });
    if (!start.isValid || !end.isValid) return null;
    return calculateIntervalTotalAmount(unit, timeZone, start.toUTC().toISO()!, end.toUTC().toISO()!);
  }, [duration, form.scheduled_end_local, form.scheduled_start_local, timeZone, unit]);

  const setStart = (nextDay: string, nextTime: string) => {
    const start = `${nextDay}T${nextTime}`;
    setForm((current) => {
      const keep = length === "custom"
        ? minutesBetween(current.scheduled_start_local, current.scheduled_end_local, timeZone) ?? 60
        : length;
      return { ...current, scheduled_start_local: start, scheduled_end_local: addMinutesLocal(start, keep, timeZone) };
    });
  };

  const chooseLength = (minutes: number) => {
    setLength(minutes);
    setForm((current) => ({ ...current, scheduled_end_local: addMinutesLocal(current.scheduled_start_local, minutes, timeZone) }));
  };

  const setCustomEnd = (value: string) => {
    // An end time earlier than the start means the booking runs past midnight.
    let end = `${day}T${value}`;
    const mins = minutesBetween(form.scheduled_start_local, end, timeZone);
    if (mins !== null && mins <= 0) end = addMinutesLocal(end, 24 * 60, timeZone);
    setForm((current) => ({ ...current, scheduled_end_local: end }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!day || !time) {
      setError(t("Choose the day and start time."));
      return;
    }
    if (duration === null || duration < 15) {
      setError(t("The booking must be at least 15 minutes."));
      return;
    }
    setError(null);
    await onSubmit(form);
  };

  return (
    <form className="grid gap-3" onSubmit={(event) => void handleSubmit(event)}>
      <h2 className="ops-page-title pe-8">{title}</h2>

      {booking?.customer_user_id ? <BookingAccountIdentity booking={booking} /> : null}

      <label className="ops-field">
        <span className="ops-label">{t("Unit")}</span>
        <select
          className="ops-select"
          disabled={!selectedVenue}
          onChange={(event) => setForm((current) => ({ ...current, unit_id: event.target.value }))}
          required
          value={form.unit_id}
        >
          {units.map((entry) => (
            <option key={entry.id} value={entry.id}>
              {entry.name}
            </option>
          ))}
        </select>
      </label>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="ops-field">
          <span className="ops-label">{t("Day")}</span>
          <input className="ops-input" onChange={(event) => setStart(event.target.value, time ?? "12:00")} required type="date" value={day ?? ""} />
        </label>
        <label className="ops-field">
          <span className="ops-label">{t("Starts at")}</span>
          <input className="ops-input" onChange={(event) => setStart(day ?? "", event.target.value)} required step={900} type="time" value={time ?? ""} />
        </label>
      </div>

      <fieldset className="grid gap-1.5">
        <legend className="ops-label mb-1">{t("How long?")}</legend>
        <div className="flex flex-wrap items-center gap-1.5">
          {LENGTHS.map((minutes) => (
            <button
              aria-pressed={length === minutes}
              className="ops-button"
              data-size="sm"
              data-variant={length === minutes ? "primary" : "secondary"}
              key={minutes}
              onClick={() => chooseLength(minutes)}
              type="button"
            >
              {formatMinutes(minutes, t)}
            </button>
          ))}
          <button
            aria-pressed={length === "custom"}
            className="ops-button"
            data-size="sm"
            data-variant={length === "custom" ? "primary" : "secondary"}
            onClick={() => setLength("custom")}
            type="button"
          >
            {t("Other")}
          </button>
          {length === "custom" ? (
            <label className="inline-flex items-center gap-2">
              <span className="ops-copy text-xs">{t("Ends at")}</span>
              <input className="ops-input w-32" onChange={(event) => setCustomEnd(event.target.value)} step={900} type="time" value={endTime} />
            </label>
          ) : null}
        </div>
      </fieldset>

      {duration !== null && duration > 0 ? (
        <p className="walkin-estimate">
          {estimate !== null
            ? t("{duration}, about {amount}", { duration: formatMinutes(duration, t), amount: formatEgp(estimate, language) })
            : formatMinutes(duration, t)}
          <span className="ops-copy block text-xs">
            {t("Until {time}", {
              time: new Intl.DateTimeFormat(language === "ar" ? "ar-EG" : "en-US", { timeZone, weekday: "short", hour: "numeric", minute: "2-digit" })
                .format(new Date(DateTime.fromISO(form.scheduled_end_local, { zone: timeZone }).toMillis())),
            })}
          </span>
        </p>
      ) : null}

      <label className="ops-field">
        <span className="ops-label">{t("Guest name")}</span>
        <input
          className="ops-input"
          maxLength={255}
          minLength={2}
          onChange={(event) => setForm((current) => ({ ...current, customer_name: event.target.value }))}
          placeholder={t("Who is the booking for?")}
          required
          value={form.customer_name}
        />
      </label>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="ops-field">
          <span className="ops-label">{t("Phone (optional)")}</span>
          <input
            className="ops-input"
            dir="ltr"
            inputMode="tel"
            onChange={(event) => setForm((current) => ({ ...current, customer_phone: event.target.value }))}
            placeholder="+20 100 000 0000"
            value={form.customer_phone}
          />
        </label>
        <div className="ops-field">
          <label className="ops-label" htmlFor="booking-contact-email">{t("Email (optional)")}</label>
          <input
            aria-describedby={booking?.customer_user_id ? "booking-contact-help" : undefined}
            className="ops-input"
            dir="ltr"
            id="booking-contact-email"
            onChange={(event) => setForm((current) => ({ ...current, customer_email: event.target.value }))}
            placeholder="guest@example.com"
            type="email"
            value={form.customer_email}
          />
          {booking?.customer_user_id ? (
            <span className="ops-copy text-xs" id="booking-contact-help">
              {t("Contact only. Changing this email does not change sign-in or booking access.")}
            </span>
          ) : null}
        </div>
      </div>

      <label className="ops-field">
        <span className="ops-label">{t("Note (optional)")}</span>
        <input
          className="ops-input"
          maxLength={1000}
          onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))}
          placeholder={t("Birthday, extra controllers…")}
          value={form.notes}
        />
      </label>

      {error ? <div className="ops-banner" data-tone="danger" role="alert">{error}</div> : null}

      <div className="flex flex-wrap justify-end gap-2">
        {onCancel && (
          <button className="ops-button" data-variant="secondary" onClick={onCancel} type="button">
            {t("Cancel")}
          </button>
        )}
        <button
          className="ops-button"
          data-variant="primary"
          disabled={isSubmitting || !selectedVenue}
          type="submit"
        >
          {isSubmitting ? t("{label}...", { label: submitLabel }) : submitLabel}
        </button>
      </div>
    </form>
  );
}
